import React from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Grid,
  Paper,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

function SummaryCards({
  showCost = false,
  summary = {},
  fixedCosts = [],
  months = 1,
  roles = [],
  showFinancial = false,
  financialData = {},
  calculateEfficiencyGains = () => ({}),
  calculateROI = () => 'N/A',
  calculatePaybackPeriod = () => 'N/A',
  showOutcome = false,
  outcomeData = {}
}) {
  const totalCost = summary.totalCost || 0;
  const totalFixed = (fixedCosts || []).reduce((sum, c) => sum + (c.monthlyCost || 0), 0) * months;
  const avgMonthlyCost = months ? totalCost / months : 0;

  return (
    <Card elevation={2} sx={{ mt: 3, mb: 3 }}>
      <CardHeader title="Summary" />
      <CardContent>
        <Grid container spacing={3}>
          {showOutcome && (
            <>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Name</Typography>
                <Typography variant="body1">{outcomeData?.name || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Status</Typography>
                <Chip label={outcomeData?.status || 'N/A'} size="small" />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">Start Date</Typography>
                <Typography variant="body1">{outcomeData?.start_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary">End Date</Typography>
                <Typography variant="body1">{outcomeData?.end_date || 'N/A'}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">Description</Typography>
                <Typography variant="body1">{outcomeData?.description || 'N/A'}</Typography>
              </Grid>
            </>
          )}

          {showCost && (
            <>
              {/* Cost Summary Cards */}
              <Grid item xs={12} md={6}>
                <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                  <Typography variant="h4" color="primary.contrastText">
                    {formatCurrency(totalCost)}
                  </Typography>
                  <Typography variant="subtitle1" color="primary.contrastText">
                    Total Project Cost
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
                      <Typography variant="h6" color="success.contrastText">
                        {formatCurrency(summary.capexAmount || 0)}
                      </Typography>
                      <Typography variant="body2" color="success.contrastText">
                        CAPEX ({summary.capexRatio || 100}%)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Box sx={{ p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                      <Typography variant="h6" color="warning.contrastText">
                        {formatCurrency(summary.opexAmount || 0)}
                      </Typography>
                      <Typography variant="body2" color="warning.contrastText">
                        OPEX ({100 - (summary.capexRatio || 100)}%)
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>

              {/* Cost Breakdown */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Cost Breakdown
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Development Team:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(summary.baseRoleCost || 0)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2">Fixed Costs:</Typography>
                  <Typography variant="body2" fontWeight="bold">
                    {formatCurrency(summary.totalFixedCost || 0)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="subtitle2">Total:</Typography>
                  <Typography variant="subtitle2" fontWeight="bold">
                    {formatCurrency(totalCost)}
                  </Typography>
                </Box>
              </Grid>

              {/* Team Summary Table */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Team Summary
                </Typography>
                {roles && roles.length > 0 ? (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 200 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Role</TableCell>
                          <TableCell align="right">Monthly Rate</TableCell>
                          <TableCell align="right">Total Cost</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {roles.map((role, index) => {
                          const totalMonths = (role.dateRanges || []).reduce((sum, dr) =>
                            sum + ((dr.endMonth || months) - (dr.startMonth || 1) + 1) * ((dr.allocation || 100) / 100), 0
                          );
                          const totalCost = (role.rate || 0) * totalMonths;
                          return (
                            <TableRow key={role.id || index}>
                              <TableCell>{role.name}</TableCell>
                              <TableCell align="right">{formatCurrency(role.rate || 0)}</TableCell>
                              <TableCell align="right">{formatCurrency(totalCost)}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No roles defined
                  </Typography>
                )}
              </Grid>

              {/* Optional: Add monthly breakdown preview if available */}
              {summary.monthlyBreakdown && (
                <Grid item xs={12}>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Monthly breakdown available in detailed view
                  </Typography>
                </Grid>
              )}
            </>
          )}

          {showFinancial && (
            <>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'primary.main', color: 'white' }}>
                  <Typography variant="h4">
                    {formatCurrency(financialData.total_development_cost)}
                  </Typography>
                  <Typography variant="body2">Total Investment</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'success.main', color: 'white' }}>
                  <Typography variant="h4">
                    {formatCurrency(typeof calculateEfficiencyGains === 'function' ? calculateEfficiencyGains().total_annual_savings : 0)}
                  </Typography>
                  <Typography variant="body2">Annual Savings</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'info.main', color: 'white' }}>
                  <Typography variant="h4">
                    {typeof calculateROI === 'function' ? calculateROI() : 'N/A'}%
                  </Typography>
                  <Typography variant="body2">ROI</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={3}>
                <Paper sx={{ p: 2, textAlign: 'center', bgcolor: 'warning.main', color: 'white' }}>
                  <Typography variant="h4">
                    {typeof calculatePaybackPeriod === 'function' ? calculatePaybackPeriod() : 'N/A'}
                  </Typography>
                  <Typography variant="body2">Payback (Years)</Typography>
                </Paper>
              </Grid>
            </>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
}

export default SummaryCards;